// Service de gestion de la file de chargement avec priorités
import { LazyLoadLogger } from './LazyLoadLogger';

export enum TaskPriority {
    P0_VIEWPORT = 0,        // Viewport actuel
    P1_PRELOAD = 1,         // Composants suivants
    P2_PAGES = 2,           // Pages accessibles
    P3_MEDIA_UPGRADE = 3    // Médias haute qualité
}

export type TaskStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface LoadTask<T = any> {
    id: string;
    priority: TaskPriority;
    execute: () => Promise<T>;
    status: TaskStatus;
    result?: T;
    error?: Error;
    startTime?: number;
    endTime?: number;
}

export interface QueueStats {
    pending: number;
    running: number;
    completed: number;
    failed: number;
    paused: boolean;
}

class LoadingQueueServiceClass {
    private queue: LoadTask[] = [];
    private running: LoadTask[] = [];
    private completed: LoadTask[] = [];
    private failed: LoadTask[] = [];
    private paused = false;
    private maxParallel = 2; // Par défaut, 2 tâches simultanées
    private pauseThreshold = 0.7; // Pause si charge > 70%
    private checkLoadInterval: number | null = null;

    /**
     * Configure le service
     */
    configure(maxParallel: number, pauseThreshold: number) {
        this.maxParallel = maxParallel;
        this.pauseThreshold = pauseThreshold;
        LazyLoadLogger.logQueue('Configuration de la queue', {
            maxParallel,
            pauseThreshold: `${(pauseThreshold * 100).toFixed(0)}%`
        });
    }

    /**
     * Ajoute une tâche à la file
     */
    enqueue<T>(task: Omit<LoadTask<T>, 'status'>): void {
        const fullTask: LoadTask<T> = {
            ...task,
            status: 'pending'
        };

        this.queue.push(fullTask);
        
        // Trier par priorité (P0 en premier)
        this.queue.sort((a, b) => a.priority - b.priority);

        LazyLoadLogger.logQueue(`Tâche ajoutée: ${task.id} (P${task.priority})`, {
            id: task.id,
            priority: this.getPriorityName(task.priority),
            queueLength: this.queue.length
        });

        // Démarrer le traitement si possible
        this.processQueue();
    }

    /**
     * Traite la file de chargement
     */
    private async processQueue(): Promise<void> {
        // Si en pause, ne rien faire
        if (this.paused) {
            LazyLoadLogger.warn('queue', 'Queue en pause, traitement suspendu');
            return;
        }

        // Si on a atteint le max de tâches parallèles, attendre
        if (this.running.length >= this.maxParallel) {
            LazyLoadLogger.logQueue(`Max parallèle atteint (${this.maxParallel}), attente...`);
            return;
        }

        // Prendre la prochaine tâche prioritaire
        const task = this.queue.shift();
        if (!task) {
            // Plus de tâches, arrêter la surveillance de charge
            if (this.checkLoadInterval) {
                clearInterval(this.checkLoadInterval);
                this.checkLoadInterval = null;
            }
            LazyLoadLogger.logQueue('Queue vide, traitement terminé');
            return;
        }

        // Démarrer la tâche
        task.status = 'running';
        task.startTime = performance.now();
        this.running.push(task);

        LazyLoadLogger.logQueue(`▶️ Démarrage tâche: ${task.id}`, {
            id: task.id,
            priority: this.getPriorityName(task.priority),
            running: this.running.length,
            pending: this.queue.length
        });

        try {
            task.result = await task.execute();
            task.status = 'completed';
            task.endTime = performance.now();
            
            const duration = task.endTime - (task.startTime || 0);
            LazyLoadLogger.logQueue(`Tâche complétée: ${task.id} (${duration.toFixed(0)}ms)`, {
                id: task.id,
                duration: `${duration.toFixed(0)}ms`
            });

            this.completed.push(task);
        } catch (error) {
            task.status = 'failed';
            task.error = error instanceof Error ? error : new Error(String(error));
            task.endTime = performance.now();
            
            LazyLoadLogger.error('queue', `Tâche échouée: ${task.id}`, error);
            this.failed.push(task);
        } finally {
            // Retirer de la liste des tâches en cours
            this.running = this.running.filter(t => t.id !== task.id);

            // Continuer le traitement
            this.processQueue();
        }
    }

    /**
     * Met en pause la queue
     */
    pause(): void {
        if (this.paused) return;

        this.paused = true;
        LazyLoadLogger.logQueue('Queue mise en pause', this.getStats());
    }

    /**
     * Reprend la queue
     */
    resume(): void {
        if (!this.paused) return;

        this.paused = false;
        LazyLoadLogger.logQueue('Queue reprise', this.getStats());

        // Relancer le traitement
        this.processQueue();
    }

    /**
     * Démarre la surveillance automatique de la charge
     */
    startLoadMonitoring(): void {
        if (this.checkLoadInterval) return;

        this.checkLoadInterval = window.setInterval(() => {
            const load = this.measureLoad();
            
            if (load > this.pauseThreshold && !this.paused) {
                LazyLoadLogger.warn('queue', `Charge élevée détectée: ${(load * 100).toFixed(0)}% - Pause automatique`, {
                    load: `${(load * 100).toFixed(0)}%`,
                    threshold: `${(this.pauseThreshold * 100).toFixed(0)}%`
                });
                this.pause();
            } else if (load < this.pauseThreshold * 0.8 && this.paused) {
                LazyLoadLogger.logQueue(`Charge normale: ${(load * 100).toFixed(0)}% - Reprise automatique`, {
                    load: `${(load * 100).toFixed(0)}%`
                });
                this.resume();
            }
        }, 1000);
    }

    /**
     * Arrête la surveillance de la charge
     */
    stopLoadMonitoring(): void {
        if (this.checkLoadInterval) {
            clearInterval(this.checkLoadInterval);
            this.checkLoadInterval = null;
        }
    }

    /**
     * Mesure la charge actuelle
     * Retourne un ratio 0-1 basé sur:
     * - Nombre de tâches en cours
     * - Taille de la queue
     */
    private measureLoad(): number {
        // Charge = (tâches en cours / max) * 0.6 + (taille queue / 20) * 0.4
        const runningLoad = this.running.length / this.maxParallel;
        const queueLoad = Math.min(this.queue.length / 20, 1);
        
        return runningLoad * 0.6 + queueLoad * 0.4;
    }

    /**
     * Récupère les statistiques de la queue
     */
    getStats(): QueueStats {
        return {
            pending: this.queue.length,
            running: this.running.length,
            completed: this.completed.length,
            failed: this.failed.length,
            paused: this.paused
        };
    }

    /**
     * Vide la queue (garde les tâches en cours)
     */
    clear(): void {
        this.queue = [];
        this.completed = [];
        this.failed = [];
        LazyLoadLogger.logQueue('Queue nettoyée');
    }

    /**
     * Annule toutes les tâches en attente d'une certaine priorité
     */
    cancelByPriority(priority: TaskPriority): void {
        const before = this.queue.length;
        this.queue = this.queue.filter(task => task.priority !== priority);
        const removed = before - this.queue.length;
        
        if (removed > 0) {
            LazyLoadLogger.logQueue(`${removed} tâches annulées (${this.getPriorityName(priority)})`);
        }
    }

    /**
     * Obtient le nom d'une priorité
     */
    private getPriorityName(priority: TaskPriority): string {
        switch (priority) {
            case TaskPriority.P0_VIEWPORT:
                return 'P0: Viewport';
            case TaskPriority.P1_PRELOAD:
                return 'P1: Préchargement';
            case TaskPriority.P2_PAGES:
                return 'P2: Pages';
            case TaskPriority.P3_MEDIA_UPGRADE:
                return 'P3: Médias HQ';
            default:
                return `P${priority}`;
        }
    }

    /**
     * Récupère une tâche par ID
     */
    getTask(id: string): LoadTask | undefined {
        return [
            ...this.queue,
            ...this.running,
            ...this.completed,
            ...this.failed
        ].find(task => task.id === id);
    }

    /**
     * Vérifie si une tâche existe déjà
     */
    hasTask(id: string): boolean {
        return this.getTask(id) !== undefined;
    }
}

// Export singleton
export const LoadingQueueService = new LoadingQueueServiceClass();

